import type {
  ActiveTask,
  MobilisationEvent,
  OnboardingTaskKind,
  TaskVisibility,
} from '@hcc/types';

/**
 * Re-derive ActiveTask[] from the event timeline on the client so we
 * don't need a parallel API for the admin tasks panel; PersonEdit
 * already fetches the chain. Shared by TasksPanel and the person
 * summary strip.
 */
export function projectTasksClientSide(events: MobilisationEvent[]): ActiveTask[] {
  const map = new Map<
    string,
    {
      assigned_event_id: string;
      title: string;
      description: string | null;
      due_at: string | null;
      visibility: TaskVisibility[];
      approval_required: boolean;
      onboarding_kind?: OnboardingTaskKind;
      assigned_by_user_id: string | null;
      assigned_at: string;
      submitted_at: string | null;
      submission_document_s3_key: string | null;
      submission_note: string | null;
      latest_review: 'approved' | 'rejected' | null;
      rejection_reason: string | null;
      approved_at: string | null;
    }
  >();
  for (const ev of events) {
    switch (ev.event_type) {
      case 'TaskAssigned': {
        const p = ev.payload as {
          task_id?: string;
          title?: string;
          description?: string;
          due_at?: string;
          visibility?: TaskVisibility[];
          approval_required?: boolean;
          onboarding_kind?: OnboardingTaskKind;
        };
        if (!p.task_id || !p.title) break;
        map.set(p.task_id, {
          assigned_event_id: ev.id,
          title: p.title,
          description: p.description ?? null,
          due_at: p.due_at ?? null,
          visibility: p.visibility ?? ['me'],
          approval_required: p.approval_required === true,
          onboarding_kind: p.onboarding_kind,
          assigned_by_user_id: ev.actor_user_id,
          assigned_at: ev.created_at,
          submitted_at: null,
          submission_document_s3_key: null,
          submission_note: null,
          latest_review: null,
          rejection_reason: null,
          approved_at: null,
        });
        break;
      }
      case 'TaskUpdated': {
        const p = ev.payload as {
          task_id?: string;
          title?: string;
          description?: string | null;
          due_at?: string | null;
          visibility?: TaskVisibility[];
          approval_required?: boolean;
        };
        if (!p.task_id) break;
        const t = map.get(p.task_id);
        if (!t) break;
        if (typeof p.title === 'string') t.title = p.title;
        if (p.description !== undefined) t.description = p.description ?? null;
        if (p.due_at !== undefined) t.due_at = p.due_at ?? null;
        if (Array.isArray(p.visibility)) t.visibility = p.visibility;
        if (typeof p.approval_required === 'boolean') {
          t.approval_required = p.approval_required;
        }
        break;
      }
      case 'TaskSubmitted': {
        const p = ev.payload as { task_id?: string; note?: string };
        if (!p.task_id) break;
        const t = map.get(p.task_id);
        if (!t) break;
        t.submitted_at = ev.created_at;
        t.submission_document_s3_key = ev.document_s3_key ?? null;
        t.submission_note = typeof p.note === 'string' ? p.note : null;
        t.latest_review = null;
        t.rejection_reason = null;
        t.approved_at = null;
        break;
      }
      case 'TaskApproved': {
        const p = ev.payload as { task_id?: string };
        if (!p.task_id) break;
        const t = map.get(p.task_id);
        if (!t) break;
        t.latest_review = 'approved';
        t.rejection_reason = null;
        t.approved_at = ev.created_at;
        break;
      }
      case 'TaskRejected': {
        const p = ev.payload as { task_id?: string; reason?: string };
        if (!p.task_id) break;
        const t = map.get(p.task_id);
        if (!t) break;
        t.latest_review = 'rejected';
        t.rejection_reason = typeof p.reason === 'string' ? p.reason : null;
        t.approved_at = null;
        break;
      }
      case 'TaskRemoved': {
        const p = ev.payload as { task_id?: string };
        if (p.task_id) map.delete(p.task_id);
        break;
      }
      default:
        break;
    }
  }
  const PRIORITY = { changes_requested: 0, awaiting_review: 1, pending: 2, complete: 3 } as const;
  return [...map.entries()]
    .map(([task_id, t]) => {
      const status: ActiveTask['status'] =
        t.latest_review === 'rejected'
          ? 'changes_requested'
          : t.latest_review === 'approved'
          ? 'complete'
          : t.submitted_at
          ? t.approval_required
            ? 'awaiting_review'
            : 'complete'
          : 'pending';
      return {
        task_id,
        assigned_event_id: t.assigned_event_id,
        title: t.title,
        description: t.description,
        due_at: t.due_at,
        visibility: t.visibility,
        approval_required: t.approval_required,
        onboarding_kind: t.onboarding_kind,
        status,
        submitted_at: t.submitted_at,
        submission_document_s3_key: t.submission_document_s3_key,
        submission_note: t.submission_note,
        rejection_reason: status === 'changes_requested' ? t.rejection_reason : null,
        completed_at: status === 'complete' ? t.approved_at ?? t.submitted_at : null,
        assigned_by_user_id: t.assigned_by_user_id,
        assigned_at: t.assigned_at,
      };
    })
    .sort((a, b) => {
      const sa = PRIORITY[a.status];
      const sb = PRIORITY[b.status];
      if (sa !== sb) return sa - sb;
      const da = a.due_at ?? '9999-12-31';
      const db = b.due_at ?? '9999-12-31';
      if (da !== db) return da < db ? -1 : 1;
      return a.assigned_at < b.assigned_at ? -1 : 1;
    });
}
